// tileSource.ts - the global pyramid's tiles: fetched, decoded, attributed.
//
// The `TileFetch` and `TileDecode` that `createTileTree` asks to be injected
// (docs/ATLAS-TERRAIN.md, "Tile tree contract"), against the format
// tools/dtm/bake_pyramid.py writes. Nothing here renders: the mesh and the tier
// wiring are a separate package, and what this module hands back is the
// `LoadedTile` shape the tree already consumes.
//
// The codec half is PURE and the wiring half is not, deliberately: the header
// parse, the dequantiser, the URL and the request plan are functions over
// bytes and a manifest, which is what lets tools/preview/tile-decode-check.mjs
// compare them against the bake's own Python readers under node. That is also
// why the one runtime import carries its `.ts` extension - node's type
// stripping resolves specifiers as written, and tsconfig has
// allowImportingTsExtensions for it.
//
// Honesty as data: docs/roadmap/03-terrain-pyramid.md, "Honesty as data". Every
// tile's provenance is the height header's `source_index` and
// `measured_fraction` joined to the body's pyramid.json, so a tile says which
// instrument measured it and how much of it was measured at all.

import {
  FACE_COUNT, boundsOf, buildClaimTexture, claimBytes, formatTileKey, parseTileKey,
  tileForLatLon,
} from './tileTree.ts';
import type {
  FetchedTile, LoadedTile, TileAlbedo, TileBounds, TileClaims, TileDecode, TileExtension, TileFetch,
  TileKey, TileProvenance,
} from './tileTree.ts';
import type { TileWorkerHandle } from './tileWorker.ts';
import * as h3 from 'h3-js';
import { wrap180HalfOpen } from '../math/angles.ts';
import { track } from '../visits/bus.ts';

// -----------------------------------------------------------------------------
// The height tile codec
// -----------------------------------------------------------------------------

/** `<4sHHHHfffII`: magic, version, cells, apron, source_index, min_m, scale_m, measured_fraction, 2 reserved. */
export const H16_HEADER_BYTES = 32;
export const H16_MAGIC = 'FXCH';
export const H16_VERSION = 1;
/** The u16 quantiser's full-scale value, so metres = min_m + q * scale_m. */
export const H16_QUANTA = 65535;
export const HEIGHT_EXT = '.h16';
export const ALBEDO_EXT = '.webp';
/** `tools/dtm/fill_artificial.py`'s sibling of the albedo tile it fills. */
export const ARTIFICIAL_EXT = '.art.webp';

export type TileRole = 'height' | 'albedo' | 'artificial';

/**
 * The roles an instrument measured, which is every role but the artificial one.
 *
 * The honesty join reads this and not `manifest.roles`: the artificial role's
 * `measured_fraction` is 0.0 by construction and nothing that reports what
 * measured a tile may ever answer with it (docs/ATLAS-TERRAIN.md, "Artificial
 * imagery"). Absence is the same story - a missing `.art.webp` is "no invented
 * ground here", not a hole in the picture.
 */
export const MEASURED_ROLES = ['height', 'albedo'] as const;

const EXT_BY_ROLE: Readonly<Record<TileRole, string>> = {
  height: HEIGHT_EXT,
  albedo: ALBEDO_EXT,
  artificial: ARTIFICIAL_EXT,
};

export interface H16Header {
  readonly magic: string;
  readonly version: number;
  /** Height samples across one tile edge, apron excluded. */
  readonly cells: number;
  readonly apron: number;
  /** Indexes the body's pyramid.json sources. */
  readonly sourceIndex: number;
  readonly minM: number;
  readonly scaleM: number;
  /** How much of this tile was measured rather than inferred, 0..1. */
  readonly measuredFraction: number;
  /** `cells + 2 * apron`: samples across one STORED edge. */
  readonly stored: number;
}

function bytesOf(buf: ArrayBuffer | ArrayBufferView): Uint8Array {
  return buf instanceof ArrayBuffer ? new Uint8Array(buf) : new Uint8Array(
    buf.buffer, buf.byteOffset, buf.byteLength,
  );
}

export function parseH16Header(buf: ArrayBuffer | ArrayBufferView): H16Header {
  const b = bytesOf(buf);
  if (b.byteLength < H16_HEADER_BYTES) {
    throw new Error(`h16: ${b.byteLength} bytes is shorter than the ${H16_HEADER_BYTES}-byte header`);
  }
  const v = new DataView(b.buffer, b.byteOffset, b.byteLength);
  let magic = '';
  for (let i = 0; i < 4; i++) magic += String.fromCharCode(b[i]!);
  if (magic !== H16_MAGIC) throw new Error(`h16: magic is ${JSON.stringify(magic)}, not ${H16_MAGIC}`);
  const version = v.getUint16(4, true);
  if (version !== H16_VERSION) throw new Error(`h16: version ${version}, not ${H16_VERSION}`);
  const cells = v.getUint16(6, true);
  const apron = v.getUint16(8, true);
  const stored = cells + 2 * apron;
  if (cells <= 0) throw new Error(`h16: ${cells} cells per edge`);
  const want = H16_HEADER_BYTES + 2 * stored * stored;
  if (b.byteLength !== want) {
    throw new Error(`h16: ${b.byteLength} bytes for a ${stored}x${stored} u16 tile, expected ${want}`);
  }
  const measuredFraction = v.getFloat32(20, true);
  if (!(measuredFraction >= 0 && measuredFraction <= 1)) {
    throw new Error(`h16: measured_fraction ${measuredFraction} is outside 0..1`);
  }
  return {
    magic,
    version,
    cells,
    apron,
    sourceIndex: v.getUint16(10, true),
    minM: v.getFloat32(12, true),
    scaleM: v.getFloat32(16, true),
    measuredFraction,
    stored,
  };
}

/**
 * Metres above the datum, row-major, `stored^2`. Rows are v, columns are u.
 *
 * `min_m + q * scale_m` in the same order the writer quantised in and with the
 * header's own float32 scale and offset, so a sample decodes to the bake's
 * float32 bit for bit rather than to within a cast. Read through a DataView
 * rather than a Uint16Array view: the format is little-endian whatever the
 * machine is, and a byte offset of 32 is not guaranteed aligned once the buffer
 * arrives as a subarray.
 */
export function dequantizeH16(buf: ArrayBuffer | ArrayBufferView): Float32Array<ArrayBuffer> {
  const header = parseH16Header(buf);
  const b = bytesOf(buf);
  const v = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const n = header.stored * header.stored;
  const out = new Float32Array(n);
  const { minM, scaleM } = header;
  for (let i = 0; i < n; i++) {
    out[i] = minM + v.getUint16(H16_HEADER_BYTES + 2 * i, true) * scaleM;
  }
  return out;
}

// -----------------------------------------------------------------------------
// Addressing
// -----------------------------------------------------------------------------

function trimSlash(base: string): string {
  return base.endsWith('/') ? base.slice(0, -1) : base;
}

/**
 * Where one tile lives, under a DTM TILE base.
 *
 * The `tiles/` segment is not decoration: `public/art/atlas/dtm/*&#47;tiles/` is the
 * git-ignore line that already keeps generated tiles out of the repo and out of
 * `vite build`'s copy of `public/`, and it is what makes `isTilePath` in
 * dtmBase.ts route a pyramid key to the tile base rather than the region base.
 *
 * `stamp` is the role's own content stamp out of pyramid.json, hung on the URL
 * as `?v=`. A tile is served immutable for a year, so without it a rebake that
 * writes the same path is invisible to every browser and edge holding the old
 * bytes. It is a required argument rather than an optional one so a new call
 * site has to say where its stamp came from; null is the pre-stamp manifest,
 * which still addresses the same object. The `/dtm/*` behaviour keys its cache
 * on this one query string (infra/tiles.ts) - CachingOptimized keys on none.
 */
export function tileUrl(
  baseUrl: string,
  key: TileKey,
  role: TileRole,
  stamp: string | null,
): string {
  const ext = EXT_BY_ROLE[role];
  const v = stamp ? `?v=${encodeURIComponent(stamp)}` : '';
  return `${trimSlash(baseUrl)}/${key.body}/tiles/${key.face}/${key.level}/${key.x}/${key.y}${ext}${v}`;
}

/** The body's pyramid.json, beside its face directories. */
export function pyramidUrl(baseUrl: string, body: string): string {
  return `${trimSlash(baseUrl)}/${body}/tiles/pyramid.json`;
}

// -----------------------------------------------------------------------------
// pyramid.json
// -----------------------------------------------------------------------------

export interface PyramidSource {
  readonly role: string;
  readonly product: string;
  readonly instrument: string;
  readonly rights: string;
  readonly url: string;
  /** The product's native posting, metres. */
  readonly gsd_m: number;
}

export interface PyramidLevel {
  readonly gsd_m: number;
  readonly oversample: number;
  readonly tiles: number;
}

/**
 * Which ground a role measured, as data.
 *
 * `fraction` is that role's share of the SPHERE - solid-angle weighted, not a
 * plain mean over tiles, because an equal-angle cubesphere cell at a face
 * corner subtends 0.79 of one at the face centre. `mask` is one byte per tile
 * at `level`, face-major then v then u, and is the coarse twin of the per-texel
 * alpha an imagery tile carries: it exists so a reader has an answer at a band
 * where no tile is resident, at a granularity it states rather than implies.
 */
export interface RoleCoverage {
  readonly fraction: number;
  readonly level: number;
  readonly mask: Uint8Array | null;
}

export interface PyramidRole {
  /** What a height header's `source_index` has to equal to mean this source. */
  readonly source_index: number;
  /** The DEEPEST level this role exists at. */
  readonly floor_level: number;
  /** The shallowest, normally 0. */
  readonly top_level: number;
  readonly cells_per_edge: number;
  readonly apron_cells: number;
  readonly stored_per_edge: number;
  readonly extension: string;
  readonly source: PyramidSource;
  readonly note: string | null;
  readonly levels: ReadonlyMap<number, PyramidLevel>;
  /** What this role measured, where the bake published it. Null on a pyramid cut
   *  before coverage was data. */
  readonly coverage: RoleCoverage | null;
  /**
   * `#rrggbb`, what an unmeasured texel of this role draws - derived at bake
   * time from the role's own measured ground (`coverageFill.ts`'s render
   * rule). Null on a pyramid cut before that field existed, or on a role with
   * nothing to derive it from; the caller falls back to the registry's own
   * `albedo_color`.
   */
  readonly unmeasured_rgb: string | null;
  /**
   * This role's tile bytes, hashed at bake time (`tools/dtm/tilestamp.py`) and
   * hung on every tile URL as `?v=`. Null on a pyramid cut before the stamp
   * existed, which addresses the unstamped object the bucket already holds.
   */
  readonly stamp: string | null;
}

/**
 * A detail region, below the global floors.
 *
 * The global roles stop where the global product does. A site bake carries on
 * under a handful of tiles, keyed the same way and indistinguishable to the
 * runtime except in its provenance (03-terrain-pyramid.md, "What goes global
 * and what stays a region"). `roots` are the tiles whose whole subtrees the
 * bake closed down to `floor_level`, so the tree's whole-level-or-nothing rule
 * holds inside a region the way it does above it - a descendant nothing
 * measured is written as parent fill with `measured_fraction` 0 rather than
 * left out.
 */
export interface PyramidExtension {
  readonly id: string;
  /** Which level the `roots` are at: where the global pyramid handed over. */
  readonly root_level: number;
  /** The shallowest level this region adds, normally `root_level + 1`. */
  readonly top_level: number;
  readonly floor_level: number;
  /** The roles the leaves carry. A HiRISE site is height only. */
  readonly roles: readonly TileRole[];
  readonly roots: readonly TileKey[];
  readonly source: PyramidSource | null;
  readonly note: string | null;
  readonly levels: ReadonlyMap<number, PyramidLevel>;
}

export interface PyramidManifest {
  readonly body: string;
  readonly datum_radius_m: number;
  readonly faces: number;
  readonly roles: ReadonlyMap<TileRole, PyramidRole>;
  /** Detail regions, in the order the manifest listed them. Empty when there are none. */
  readonly extensions: readonly PyramidExtension[];
}

function asRecord(v: unknown, what: string): Readonly<Record<string, unknown>> {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) {
    throw new Error(`pyramid.json: ${what} is not an object`);
  }
  return v as Readonly<Record<string, unknown>>;
}

function asNumber(v: unknown, what: string): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    throw new Error(`pyramid.json: ${what} is not a finite number`);
  }
  return v;
}

function asString(v: unknown, what: string): string {
  if (typeof v !== 'string' || v.length === 0) {
    throw new Error(`pyramid.json: ${what} is not a non-empty string`);
  }
  return v;
}

/**
 * `allowEmptyUrl` is the artificial role and nothing else.
 *
 * A measured role's source is a product somebody published and its citation is
 * required - that check has caught a role cut with no attribution. The
 * artificial role is generated here (`tools/dtm/fill_artificial.py`) and has
 * no publication to cite, which is what `rights: "Derived, not measured"`
 * already says, so its url is empty by design and the rule is loosened for
 * that role alone rather than for the parse.
 */
function parseSource(v: unknown, what: string, allowEmptyUrl = false): PyramidSource {
  const r = asRecord(v, what);
  const url = r['url'];
  return {
    role: asString(r['role'], `${what}.role`),
    product: asString(r['product'], `${what}.product`),
    instrument: asString(r['instrument'], `${what}.instrument`),
    rights: asString(r['rights'], `${what}.rights`),
    url: allowEmptyUrl && url === '' ? '' : asString(url, `${what}.url`),
    gsd_m: asNumber(r['gsd_m'], `${what}.gsd_m`),
  };
}

/** base64 to bytes, the one place a manifest carries any. */
function decodeBase64(text: string, what: string): Uint8Array {
  const bin = typeof atob === 'function' ? atob(text) : '';
  if (bin === '' && text !== '') throw new Error(`pyramid.json: ${what} is not base64`);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

function parseCoverage(r: Readonly<Record<string, unknown>>, name: TileRole): RoleCoverage | null {
  const fraction = r['measured_fraction'];
  if (typeof fraction !== 'number' || !Number.isFinite(fraction)) return null;
  const raw = r['coverage'];
  if (raw === undefined || raw === null) return { fraction, level: 0, mask: null };
  const c = asRecord(raw, `roles.${name}.coverage`);
  const level = asNumber(c['level'], `roles.${name}.coverage.level`);
  const mask = decodeBase64(asString(c['values'], `roles.${name}.coverage.values`),
    `roles.${name}.coverage.values`);
  const want = FACE_COUNT * (1 << level) * (1 << level);
  if (mask.length !== want) {
    throw new Error(`pyramid.json: roles.${name}.coverage has ${mask.length} bytes, `
      + `not the ${want} an L${level} mask is`);
  }
  return { fraction, level, mask };
}

/**
 * What fraction of a role's imagery was measured under a direction, 0..1, or
 * null where the pyramid publishes no mask.
 *
 * The answer is the mask's own cell, so it is as coarse as the mask says it is;
 * the caller reports that granularity rather than implying a per-texel answer.
 */
export function coverageAt(
  manifest: PyramidManifest,
  role: TileRole,
  latRad: number,
  lonRad: number,
): { fraction: number; level: number } | null {
  const cov = manifest.roles.get(role)?.coverage ?? null;
  if (!cov || !cov.mask) return null;
  const key = tileForLatLon(manifest.body, latRad, lonRad, cov.level);
  const n = 1 << cov.level;
  return {
    fraction: (cov.mask[(key.face * n + key.y) * n + key.x] ?? 0) / 255,
    level: cov.level,
  };
}

function parseRole(v: unknown, name: TileRole): PyramidRole {
  const r = asRecord(v, `roles.${name}`);
  const levels = new Map<number, PyramidLevel>();
  for (const [k, raw] of Object.entries(asRecord(r['levels'], `roles.${name}.levels`))) {
    const l = asRecord(raw, `roles.${name}.levels.${k}`);
    levels.set(Number(k), {
      gsd_m: asNumber(l['gsd_m'], `roles.${name}.levels.${k}.gsd_m`),
      oversample: asNumber(l['oversample'], `roles.${name}.levels.${k}.oversample`),
      tiles: asNumber(l['tiles'], `roles.${name}.levels.${k}.tiles`),
    });
  }
  const note = r['note'];
  const unmeasuredRgb = r['unmeasured_rgb'];
  const stamp = r['stamp'];
  return {
    stamp: typeof stamp === 'string' && stamp.length > 0 ? stamp : null,
    coverage: parseCoverage(r, name),
    source_index: asNumber(r['source_index'], `roles.${name}.source_index`),
    floor_level: asNumber(r['floor_level'], `roles.${name}.floor_level`),
    top_level: asNumber(r['top_level'], `roles.${name}.top_level`),
    cells_per_edge: asNumber(r['cells_per_edge'], `roles.${name}.cells_per_edge`),
    apron_cells: asNumber(r['apron_cells'], `roles.${name}.apron_cells`),
    stored_per_edge: asNumber(r['stored_per_edge'], `roles.${name}.stored_per_edge`),
    extension: asString(r['extension'], `roles.${name}.extension`),
    source: parseSource(r['source'], `roles.${name}.source`, name === 'artificial'),
    note: typeof note === 'string' && note.length > 0 ? note : null,
    unmeasured_rgb: typeof unmeasuredRgb === 'string' && /^#[0-9a-fA-F]{6}$/.test(unmeasuredRgb)
      ? unmeasuredRgb : null,
    levels,
  };
}

/**
 * A root key, as `<face>/<L>/<x>/<y>` or with the body in front.
 *
 * The bake writes the region index beside the roles, where every key is already
 * under one body, so the body segment is redundant there and optional here. It
 * is checked when it is present, because a root under the wrong body would
 * silently extend nothing.
 */
function parseRootKey(v: unknown, body: string, what: string): TileKey {
  if (typeof v === 'string') {
    const parts = v.split('/');
    return parseTileKey(parts.length === 4 ? `${body}/${v}` : v);
  }
  const r = asRecord(v, what);
  const key: TileKey = {
    body,
    face: asNumber(r['face'], `${what}.face`),
    level: asNumber(r['level'], `${what}.level`),
    x: asNumber(r['x'], `${what}.x`),
    y: asNumber(r['y'], `${what}.y`),
  };
  return parseTileKey(formatTileKey(key));
}

function parseExtension(v: unknown, body: string, id: string): PyramidExtension {
  const what = `extensions.${id}`;
  const r = asRecord(v, what);
  const floor = asNumber(r['floor_level'], `${what}.floor_level`);
  const rawRoots = r['roots'];
  if (!Array.isArray(rawRoots) || rawRoots.length === 0) {
    throw new Error(`pyramid.json: ${what}.roots is not a non-empty array`);
  }
  const roots = rawRoots.map((k, i) => parseRootKey(k, body, `${what}.roots[${i}]`));
  const rootLevel = r['root_level'] === undefined
    ? roots[0]!.level
    : asNumber(r['root_level'], `${what}.root_level`);
  for (const root of roots) {
    if (root.level !== rootLevel) {
      throw new Error(`pyramid.json: ${what} root ${formatTileKey(root)} is not at level ${rootLevel}`);
    }
  }
  const top = r['top_level'] === undefined
    ? rootLevel + 1
    : asNumber(r['top_level'], `${what}.top_level`);
  if (!(top > rootLevel && floor >= top)) {
    throw new Error(`pyramid.json: ${what} levels ${rootLevel} -> ${top}..${floor} are not a descent`);
  }
  const roleNames: TileRole[] = [];
  const rawRoles = r['roles'] ?? ['height'];
  if (!Array.isArray(rawRoles) || rawRoles.length === 0) {
    throw new Error(`pyramid.json: ${what}.roles is not a non-empty array`);
  }
  for (const name of rawRoles) {
    if (name !== 'height' && name !== 'albedo') {
      throw new Error(`pyramid.json: ${what}.roles has ${JSON.stringify(name)}`);
    }
    roleNames.push(name);
  }
  const levels = new Map<number, PyramidLevel>();
  if (r['levels'] !== undefined) {
    for (const [k, raw] of Object.entries(asRecord(r['levels'], `${what}.levels`))) {
      const l = asRecord(raw, `${what}.levels.${k}`);
      levels.set(Number(k), {
        gsd_m: asNumber(l['gsd_m'], `${what}.levels.${k}.gsd_m`),
        oversample: asNumber(l['oversample'], `${what}.levels.${k}.oversample`),
        tiles: asNumber(l['tiles'], `${what}.levels.${k}.tiles`),
      });
    }
  }
  const note = r['note'];
  return {
    id,
    root_level: rootLevel,
    top_level: top,
    floor_level: floor,
    roles: roleNames,
    roots,
    source: r['source'] === undefined ? null : parseSource(r['source'], `${what}.source`),
    note: typeof note === 'string' && note.length > 0 ? note : null,
    levels,
  };
}

/**
 * The body index, validated.
 *
 * The bake writes the sources INSIDE each role rather than as a root list, so
 * the `source_index` join is over the roles - see `sourceOf`.
 */
export function parsePyramidManifest(raw: unknown): PyramidManifest {
  const r = asRecord(raw, 'root');
  const roles = new Map<TileRole, PyramidRole>();
  const rolesRaw = asRecord(r['roles'], 'roles');
  // Artificial last, so `rolesForLevel`'s order still puts a measured role
  // first for every reader that takes the head of it.
  for (const name of ['height', 'albedo', 'artificial'] as const) {
    if (rolesRaw[name] === undefined) continue;
    const role = parseRole(rolesRaw[name], name);
    const want = EXT_BY_ROLE[name];
    if (role.extension !== want) {
      throw new Error(`pyramid.json: roles.${name}.extension is ${role.extension}, not ${want}`);
    }
    if (role.stored_per_edge !== role.cells_per_edge + 2 * role.apron_cells) {
      throw new Error(`pyramid.json: roles.${name} stored_per_edge disagrees with cells + 2*apron`);
    }
    roles.set(name, role);
  }
  // A pyramid whose only role is artificial is the honest shape for a body
  // nothing has imaged (mimas), so it parses - but the measured roles keep
  // every rule they had: an albedo block that is there is still checked whole,
  // and a manifest with no role at all is still a refusal.
  if (roles.size === 0) throw new Error('pyramid.json: no height, albedo or artificial role');
  const body = asString(r['body'], 'body');
  // Absent on a body with no site bake, an array once there is one, and an
  // object keyed by site id is accepted too: the bake indexes its regions by
  // name everywhere else and this is the one place it would have to flatten.
  const extRaw = r['extensions'];
  const extensions: PyramidExtension[] = [];
  if (Array.isArray(extRaw)) {
    extRaw.forEach((v, i) => {
      const id = typeof asRecord(v, `extensions[${i}]`)['id'] === 'string'
        ? String(asRecord(v, `extensions[${i}]`)['id']) : String(i);
      extensions.push(parseExtension(v, body, id));
    });
  } else if (extRaw !== undefined && extRaw !== null) {
    for (const [id, v] of Object.entries(asRecord(extRaw, 'extensions'))) {
      extensions.push(parseExtension(v, body, id));
    }
  }
  return {
    body,
    datum_radius_m: asNumber(r['datum_radius_m'], 'datum_radius_m'),
    faces: asNumber(r['faces'], 'faces'),
    roles,
    extensions,
  };
}

/**
 * The extension whose subtree holds this key, or null.
 *
 * A key is inside a region when its ancestor at the region's root level is one
 * of the roots, which is a shift on the key rather than a geometric test.
 */
export function extensionFor(
  manifest: PyramidManifest,
  key: TileKey,
): PyramidExtension | null {
  for (const ext of manifest.extensions) {
    if (key.level < ext.root_level) continue;
    const k = key.level - ext.root_level;
    const ancestor = `${key.body}/${key.face}/${ext.root_level}/${key.x >> k}/${key.y >> k}`;
    if (ext.roots.some((root) => formatTileKey(root) === ancestor)) return ext;
  }
  return null;
}

/** The regions, in the shape `createTileTree` takes them. */
export function treeExtensions(manifest: PyramidManifest): TileExtension[] {
  return manifest.extensions.map((ext) => ({
    id: ext.id,
    floorLevel: ext.floor_level,
    roots: ext.roots,
  }));
}

/**
 * The deepest level that exists anywhere on this body, region floors included.
 *
 * Measured roles only: the artificial role must never be what carries a
 * descent one level further, or the ground closest to the eye would be the
 * invented one.
 */
export function deepestLevel(manifest: PyramidManifest): number {
  let deepest = 0;
  for (const name of MEASURED_ROLES) {
    const role = manifest.roles.get(name);
    if (role) deepest = Math.max(deepest, role.floor_level);
  }
  for (const ext of manifest.extensions) deepest = Math.max(deepest, ext.floor_level);
  return deepest;
}

/**
 * The deepest GLOBAL level: the traversal's own floor before a region.
 *
 * The deepest of the roles rather than height's, which is what this used to be
 * read as. Height is the deeper of the two wherever both are baked - Mars L7
 * against Viking's L6 - so the answer does not move for a body with a height
 * pyramid; on an imagery-only body height is not there at all and reading it
 * gave 0, which held the whole albedo pyramid at its root set.
 */
export function deepestGlobalLevel(manifest: PyramidManifest): number {
  let deepest = 0;
  for (const name of MEASURED_ROLES) {
    const role = manifest.roles.get(name);
    if (role) deepest = Math.max(deepest, role.floor_level);
  }
  return deepest;
}

/** The source a height header's `source_index` names. Measured roles only. */
export function sourceOf(manifest: PyramidManifest, sourceIndex: number): PyramidSource | null {
  for (const name of MEASURED_ROLES) {
    const role = manifest.roles.get(name);
    if (role && role.source_index === sourceIndex) return role.source;
  }
  return null;
}

/**
 * Which roles a tile at this level has at all.
 *
 * Height and albedo stop at different levels because the products do - Mars
 * height reaches L7 and its Viking albedo stops at L6 - so a level past a
 * role's floor is an ABSENCE, not a fetch that will 404.
 */
export function rolesForLevel(manifest: PyramidManifest, level: number): readonly TileRole[] {
  const out: TileRole[] = [];
  for (const [name, role] of manifest.roles) {
    if (level >= role.top_level && level <= role.floor_level) out.push(name);
  }
  return out;
}

/**
 * Which roles this TILE has, region floors included.
 *
 * Below every global floor a level exists only inside a detail region, so the
 * answer stops being a property of the level alone: two L9 keys a thousand
 * kilometres apart have different answers, and the one outside every region has
 * none at all.
 */
export function rolesForTile(manifest: PyramidManifest, key: TileKey): readonly TileRole[] {
  const global = rolesForLevel(manifest, key.level);
  if (global.length > 0) return global;
  const ext = extensionFor(manifest, key);
  if (!ext || key.level < ext.top_level || key.level > ext.floor_level) return [];
  return ext.roles;
}

// -----------------------------------------------------------------------------
// Provenance
// -----------------------------------------------------------------------------

export type TileAbsenceReason = 'above-floor' | 'not-found';

export interface TileAbsence {
  readonly role: TileRole;
  readonly reason: TileAbsenceReason;
}

export interface ProvenanceInput {
  /** The height header, when one was fetched. Its fraction is the measured one. */
  readonly header?: H16Header | null;
  readonly absent?: readonly TileAbsence[];
}

/**
 * `TileProvenance` for a key, from the manifest and whatever the tile itself said.
 *
 * `measured_fraction` is READ from the height header when there is one. There is
 * no per-tile fraction anywhere else: WebP has nowhere to put a float, so the
 * bake keeps an albedo tile's fraction in provenance.json (73 MB for Mars, one
 * record per tile) which no runtime is going to fetch. So an albedo-only tile's
 * fraction is assumed - 1 for a tile the bake wrote, 0 for one that is not
 * there - and the note says it was assumed rather than measured.
 */
export function provenanceFor(
  manifest: PyramidManifest,
  key: TileKey,
  input: ProvenanceInput = {},
): TileProvenance {
  const absent = input.absent ?? [];
  const header = input.header ?? null;
  // Measured roles only. A tile whose sole role is artificial has measured
  // NOTHING, and this is the one place a reader could otherwise have read it
  // as a measured tile with a fraction of 1.
  const roles = rolesForTile(manifest, key).filter(
    (r): r is 'height' | 'albedo' => r !== 'artificial',
  );
  // Only the roles this level HAS can be missing from it: an albedo past its
  // floor is the pyramid's shape, not a hole in this tile.
  const missing = absent.filter((a) => a.role !== 'artificial' && roles.includes(a.role));
  const nothingHere = roles.length === 0 || missing.length === roles.length;
  const role = manifest.roles.get(roles[0] ?? 'height') ?? null;
  // Below every global floor the tile belongs to a region, and the region's
  // instrument is the honest answer even though the header's source_index
  // indexes the global roles.
  const ext = key.level > (role?.floor_level ?? -1) ? extensionFor(manifest, key) : null;
  const source = ext?.source
    ?? (header !== null ? sourceOf(manifest, header.sourceIndex) : null)
    ?? role?.source ?? null;
  const notes: string[] = [];
  if (ext?.note) notes.push(ext.note);
  else if (role?.note) notes.push(role.note);
  const notFound = missing.filter((a) => a.reason === 'not-found').map((a) => a.role);
  if (notFound.length > 0) notes.push(`no data: ${notFound.join(', ')}`);
  else if (header === null) {
    notes.push('measured_fraction assumed: no height tile at this level to read it from');
  }
  const level = ext?.levels.get(key.level) ?? role?.levels.get(key.level);
  return {
    instrument: nothingHere ? 'none' : source?.instrument ?? 'unknown',
    rights: source?.rights ?? 'unknown',
    url: source?.url ?? '',
    measured_fraction: header !== null ? header.measuredFraction : nothingHere ? 0 : 1,
    ...(source ? { product_id: source.product, native_posting_m: source.gsd_m } : {}),
    ...(level ? { oversample: level.oversample } : {}),
    ...(notes.length > 0 ? { note: notes.join('; ') } : {}),
  };
}

// -----------------------------------------------------------------------------
// Credit
// -----------------------------------------------------------------------------

export type CreditTier = 'terrain' | 'imagery' | 'detail';

export interface CreditSource {
  readonly tier: CreditTier;
  readonly source: PyramidSource;
}

/**
 * Every source drawn for one tile, height and albedo counted apart: a global
 * tile draws both at once, from different products.
 *
 * A region leaf below the height floor credits 'detail' alone - its own
 * orthoimage rides the same HiRISE product as its height (`ATLAS-TERRAIN.md`,
 * "The site leaves, and the imagery over them"), so there is no separate
 * 'imagery' entry for it.
 */
export function creditSourcesForTile(manifest: PyramidManifest, key: TileKey): readonly CreditSource[] {
  const out: CreditSource[] = [];
  const heightRole = manifest.roles.get('height');
  const albedoRole = manifest.roles.get('albedo');
  const overHeightFloor = heightRole !== undefined && key.level > heightRole.floor_level;
  const ext = overHeightFloor ? extensionFor(manifest, key) : null;
  if (ext?.source) {
    out.push({ tier: 'detail', source: ext.source });
  } else if (heightRole && key.level >= heightRole.top_level && key.level <= heightRole.floor_level) {
    out.push({ tier: 'terrain', source: heightRole.source });
  }
  if (albedoRole && key.level >= albedoRole.top_level && key.level <= albedoRole.floor_level) {
    out.push({ tier: 'imagery', source: albedoRole.source });
  }
  return out;
}

// -----------------------------------------------------------------------------
// The fetch
// -----------------------------------------------------------------------------

/** What a pyramid fetch hands back: `FetchedTile` plus why a payload is missing. */
export interface FetchedPyramidTile extends FetchedTile {
  /** A MEASURED role that is not here, and whether that is the floor or a hole. */
  readonly absent: readonly TileAbsence[];
  /** The height header, parsed before the buffer is transferred away. */
  readonly header: H16Header | null;
}

export type FetchLike = (url: string, init: { signal: AbortSignal }) => Promise<Response>;

interface RoleFetchResult {
  readonly role: TileRole;
  readonly buffer: ArrayBuffer | null;
  readonly reason: TileAbsenceReason | null;
}

/** `RIFF....WEBP`: the first twelve bytes of every WebP file. */
function isWebp(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 12) return false;
  const b = new Uint8Array(buffer, 0, 12);
  return b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46
    && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50;
}

async function fetchRole(
  fetchImpl: FetchLike,
  baseUrl: string,
  key: TileKey,
  role: TileRole,
  stamp: string | null,
  signal: AbortSignal,
): Promise<RoleFetchResult> {
  const res = await fetchImpl(tileUrl(baseUrl, key, role, stamp), { signal });
  // A 404 is a no-data tile, not a failure: the bake writes an explicit no-data
  // record for one and the tree must be able to cache the hole rather than
  // spend its three attempts on it every time the camera comes back.
  if (res.status === 404) return { role, buffer: null, reason: 'not-found' };
  if (!res.ok) {
    throw new Error(`tile ${formatTileKey(key)} ${role}: HTTP ${res.status} ${res.statusText}`);
  }
  const buffer = await res.arrayBuffer();
  // The artificial role is the only SPARSE one - a tile exists where the
  // measured role left a hole and nowhere else - and the dev server answers a
  // path it has no file for with the app's own index.html at 200, so absence
  // cannot be read off the status there. Not a WebP is not a fill.
  if (role === 'artificial' && !isWebp(buffer)) return { role, buffer: null, reason: 'not-found' };
  return { role, buffer, reason: null };
}

// -----------------------------------------------------------------------------
// The handle
// -----------------------------------------------------------------------------

export interface TileSourceConfig {
  readonly body: string;
  /**
   * The DTM tile base: `DTM_TILE_BASE` from dtmBase.ts, which is
   * `/art/atlas/dtm` in dev and `/dtm` in a deploy. Passed in rather than read
   * here so this module has no `import.meta.env` and stays loadable under node.
   */
  readonly baseUrl: string;
  /** Where the decode runs. One is made and owned here if none is given. */
  readonly worker?: TileWorkerHandle | null;
  /** For the check's stub. Defaults to the global `fetch`. */
  readonly fetchImpl?: FetchLike;
  /** For the check's stub. Defaults to `createTileWorker`. */
  readonly makeWorker?: () => TileWorkerHandle;
  /**
   * `descent-dojo --inject height-role`: splice another body's role into this
   * manifest and read its tiles from that body's pyramid.
   *
   * The control for the imagery floor. A role that is not in the manifest
   * cannot be corrupted in place, so the donor's own role block is taken whole
   * - levels, apron and stored edge included - and only the URL's body segment
   * moves. The ground that arrives is the donor's, which is the point: the
   * reading is that the descent goes further and fetches height at all.
   */
  readonly injectRole?: { readonly role: TileRole; readonly from: string } | null;
  /**
   * `descent-dojo --artificial`'s crossing leg: open the pyramid as if this
   * role were not in it.
   *
   * A role's tiles and its manifest are both git-ignored and reach a deployed
   * stage only through `tools/dtm/sync_tiles.py`, so a stage whose bucket
   * predates a role serves a manifest without it - the state the fill was
   * reported blanking in. Dropping the block here reproduces that from a tree
   * that HAS the tiles: nothing is requested for the role and no tile carries
   * it, which is what the fallback has to survive.
   */
  readonly dropRole?: TileRole | null;
  /**
   * What to bake a decoding tile's claim texture against, read fresh at every
   * decode: the resolution and the claimed set can both move between when a
   * tile was FETCHED and when it DECODES. Null (the default; also atlas.ts's
   * answer before h3-js and the registry resolution are both ready) skips the
   * claim texture and leaves `LoadedTile.claims` null.
   */
  readonly getClaimState?: () => ClaimState | null;
  /**
   * Whether the ARTIFICIAL FILL layer is on, read fresh at every fetch.
   *
   * The role is requested only while it is, so the layer off costs not one
   * `.art.webp` request - which is what `descent-dojo --artificial`'s request
   * bar reads. A 404 with it on is "no artificial data here", the same absence
   * the tree already reads off a measured role.
   */
  readonly wantArtificial?: () => boolean;
}

export interface TileSource {
  /** Resolves once pyramid.json is in, which is what says a body's max level. */
  ready(): Promise<PyramidManifest>;
  /** The manifest, or null before `ready()` has resolved. */
  manifest(): PyramidManifest | null;
  readonly fetch: TileFetch;
  readonly decode: TileDecode;
  /** What the manifest says about this key, before any tile of it is read. */
  provenanceFor(key: TileKey): TileProvenance | null;
  /**
   * Tile requests issued per role since this source opened.
   *
   * Counted where the request is made rather than derived from a flag: what
   * `imagery-floor` has to read is that no height tile was ASKED for on a body
   * whose manifest declares none.
   */
  roleFetches(): Readonly<Record<TileRole, number>>;
  dispose(): void;
}

export function createTileSource(config: TileSourceConfig): TileSource {
  const { body, baseUrl } = config;
  const fetchImpl: FetchLike = config.fetchImpl
    ?? ((url, init) => globalThis.fetch(url, init));
  const lifetime = new AbortController();
  let manifest: PyramidManifest | null = null;
  let manifestPromise: Promise<PyramidManifest> | null = null;
  let worker: TileWorkerHandle | null = config.worker ?? null;
  const ownsWorker = !config.worker;
  let disposed = false;
  const injectRole = config.injectRole ?? null;
  const dropRole = config.dropRole ?? null;
  const fetches: Record<TileRole, number> = { height: 0, albedo: 0, artificial: 0 };

  function ready(): Promise<PyramidManifest> {
    if (manifestPromise) return manifestPromise;
    manifestPromise = (async () => {
      const res = await fetchImpl(pyramidUrl(baseUrl, body), { signal: lifetime.signal });
      if (!res.ok) {
        throw new Error(`pyramid.json for ${body}: HTTP ${res.status} ${res.statusText}`);
      }
      const parsed = parsePyramidManifest(await res.json());
      if (parsed.body !== body) {
        throw new Error(`pyramid.json under ${body} says body ${parsed.body}`);
      }
      const spliced = injectRole ? await spliceRole(parsed, injectRole) : parsed;
      manifest = dropRole ? withoutRole(spliced, dropRole) : spliced;
      return manifest;
    })();
    manifestPromise.catch((e: unknown) => { if (!disposed) track('exception', { kind: 'manifest', detail: e instanceof Error ? e.message : String(e), body_id: body, fatal: false }); });
    return manifestPromise;
  }

  function ensureWorker(): TileWorkerHandle | null {
    if (worker && worker.live) return worker;
    if (!ownsWorker || disposed || !config.makeWorker) return null;
    // A handle that stopped being live still holds its thread and its two
    // listeners, so it goes back before its replacement is made.
    worker?.dispose();
    worker = config.makeWorker();
    return worker;
  }

  const fetchTile: TileFetch = async (key, signal): Promise<FetchedPyramidTile> => {
    signal.throwIfAborted();
    const m = await ready();
    signal.throwIfAborted();
    const wantArt = config.wantArtificial?.() ?? false;
    const roles = rolesForTile(m, key).filter((r) => r !== 'artificial' || wantArt);
    const absent: TileAbsence[] = [];
    for (const name of MEASURED_ROLES) {
      if (m.roles.has(name) && !roles.includes(name)) {
        absent.push({ role: name, reason: 'above-floor' });
      }
    }
    const got = await Promise.all(
      roles.map((role) => {
        fetches[role]++;
        return fetchRole(fetchImpl, baseUrl, urlKeyFor(key, role), role,
                         m.roles.get(role)?.stamp ?? null, signal);
      }),
    );
    signal.throwIfAborted();
    let height: ArrayBuffer | null = null;
    let albedo: ArrayBuffer | null = null;
    let artificial: ArrayBuffer | null = null;
    for (const r of got) {
      // An artificial 404 is not an absence a reader hears about: nothing was
      // measured there either way, and the note belongs to the measured roles.
      if (r.role === 'artificial') artificial = r.buffer;
      else if (r.reason) absent.push({ role: r.role, reason: r.reason });
      else if (r.role === 'height') height = r.buffer;
      else albedo = r.buffer;
    }
    const header = height ? parseH16Header(height) : null;
    if (header) {
      const role = m.roles.get('height');
      if (role && header.stored !== role.stored_per_edge) {
        throw new Error(
          `tile ${formatTileKey(key)}: header says ${header.stored} stored samples per edge, `
          + `pyramid.json says ${role.stored_per_edge}`,
        );
      }
    }
    return {
      key, height, albedo, artificial,
      provenance: provenanceFor(m, key, { header, absent }), absent, header,
    };
  };

  const decodeTile: TileDecode = async (fetched, signal): Promise<LoadedTile> => {
    signal.throwIfAborted();
    const m = manifest ?? (await ready());
    const { key } = fetched;
    const radiusM = m.datum_radius_m;
    const w = ensureWorker();
    const claimState = config.getClaimState?.() ?? null;
    const art = (fetched as FetchedPyramidTile).artificial ?? null;
    const decoded = w
      ? await w.decodeTile({
        key, radiusM, height: fetched.height, albedo: fetched.albedo, artificial: art,
        claimRes: claimState?.res ?? null, claimCells: claimState?.cells ?? null,
      })
      : await decodeTilePayloads(key, radiusM, fetched.height, fetched.albedo, claimState, art);
    if (signal.aborted) {
      // The worker made these bitmaps; nothing else will close them.
      closeAlbedo(decoded.albedo);
      closeAlbedo(decoded.artificial);
      signal.throwIfAborted();
    }
    // Whatever the worker could not turn into a bitmap comes back as the bytes
    // it went in as, and finishes here. Which thread that is depends on the
    // browser: createImageBitmap exists in a module worker in Chrome, Firefox
    // and Safari 16.4+, and this is the path for anything older.
    let albedo = decoded.albedo;
    if (!albedo && decoded.albedoRaw) albedo = await decodeAlbedo(decoded.albedoRaw);
    let artificial = decoded.artificial;
    if (!artificial && decoded.artificialRaw) {
      // A fill that will not decode is no fill, never a failed tile: the
      // measured ground under it is what the frame owes the eye.
      artificial = await decodeAlbedo(decoded.artificialRaw).catch(() => null);
    }
    if (signal.aborted) {
      closeAlbedo(albedo);
      closeAlbedo(artificial);
      signal.throwIfAborted();
    }
    const height = decoded.height;
    // The real decoded cost, not the wire cost: a 258^2 float field is 266 KB
    // against 133 KB of quantised bytes, and a 512^2 bitmap is 1 MB against
    // about 79 KB of WebP.
    const bytes = (height ? height.byteLength : 0)
      + (albedo ? albedo.width * albedo.height * 4 : 0)
      + (artificial ? artificial.width * artificial.height * 4 : 0)
      + (decoded.claims ? claimBytes(decoded.claims) : 0);
    let live = true;
    return {
      key,
      level: key.level,
      bounds: decoded.bounds,
      height,
      albedo,
      artificial,
      claims: decoded.claims,
      provenance: fetched.provenance,
      bytes,
      dispose(): void {
        if (!live) return;
        live = false;
        closeAlbedo(albedo);
        closeAlbedo(artificial);
      },
    };
  };

  async function spliceRole(
    into: PyramidManifest,
    inject: { role: TileRole; from: string },
  ): Promise<PyramidManifest> {
    const res = await fetchImpl(pyramidUrl(baseUrl, inject.from), { signal: lifetime.signal });
    if (!res.ok) {
      throw new Error(`pyramid.json for the injected ${inject.role} role from ${inject.from}: `
        + `HTTP ${res.status} ${res.statusText}`);
    }
    const donor = parsePyramidManifest(await res.json());
    const role = donor.roles.get(inject.role);
    if (!role) {
      throw new Error(`${inject.from}'s pyramid has no ${inject.role} role to inject`);
    }
    const roles = new Map(into.roles);
    roles.set(inject.role, role);
    return { ...into, roles };
  }

  /** The same manifest with one role's block gone, extensions included. */
  function withoutRole(from: PyramidManifest, role: TileRole): PyramidManifest {
    const roles = new Map(from.roles);
    if (!roles.delete(role)) return from;
    return {
      ...from,
      roles,
      extensions: from.extensions.map((ext) => (ext.roles.includes(role)
        ? { ...ext, roles: ext.roles.filter((r) => r !== role) }
        : ext)),
    };
  }

  /** Which body's pyramid a role's tiles come from: the donor's, when injected. */
  const urlKeyFor = (key: TileKey, role: TileRole): TileKey =>
    (injectRole && injectRole.role === role ? { ...key, body: injectRole.from } : key);

  return {
    ready,
    manifest: () => manifest,
    fetch: fetchTile,
    decode: decodeTile,
    provenanceFor(key: TileKey): TileProvenance | null {
      return manifest ? provenanceFor(manifest, key) : null;
    },
    roleFetches: () => ({ ...fetches }),
    dispose(): void {
      if (disposed) return;
      disposed = true;
      lifetime.abort(new Error(`tile source for ${body} disposed`));
      if (ownsWorker) worker?.dispose();
      worker = null;
    },
  };
}

// -----------------------------------------------------------------------------
// The decode itself, called on whichever thread has the bytes
// -----------------------------------------------------------------------------

/** `TileAlbedo` narrowed to the bitmap the decode actually produces, which is transferable. */
export interface DecodedAlbedo {
  readonly width: number;
  readonly height: number;
  readonly source: ImageBitmap;
}

/** What one tile's payloads decode to, short of the provenance join. */
export interface DecodedTilePayloads {
  readonly height: Float32Array<ArrayBuffer> | null;
  readonly albedo: DecodedAlbedo | null;
  /** The albedo bytes, handed back undecoded because this thread could not. */
  readonly albedoRaw: ArrayBuffer | null;
  /** The artificial fill over this tile's unmeasured texels, or null for none. */
  readonly artificial: DecodedAlbedo | null;
  readonly artificialRaw: ArrayBuffer | null;
  readonly bounds: TileBounds;
  readonly claims: TileClaims | null;
}

/** The resolution to bake a tile's claim texture at, and the aggregated claimed set there. */
export interface ClaimState {
  readonly res: number;
  readonly cells: readonly string[];
}

export function closeAlbedo(albedo: TileAlbedo | null): void {
  if (albedo && typeof ImageBitmap !== 'undefined' && albedo.source instanceof ImageBitmap) {
    albedo.source.close();
  }
}

export async function decodeAlbedo(raw: ArrayBuffer): Promise<DecodedAlbedo> {
  // NOT flipped, and the texture over it carries `flipY = false` to match: the
  // WebGL unpack flip does not apply to an ImageBitmap source, so any flip has
  // to be asked for here (`imageBitmap.ts`). A pyramid tile wants none - the
  // bake writes its first row at the tile's v0 edge (`tools/dtm/bake_pyramid.py`
  // samples row j at ascending v), which `tileAlbedoUv.ts` reads at t = 0.
  // `premultiplyAlpha: 'none'`, because an imagery tile's ALPHA IS ITS COVERAGE
  // and its RGB under an unmeasured texel is the body's own albedo_color. The
  // browser default would multiply that colour by a zero alpha and leave black
  // to bleed out of every hole under filtering.
  const bitmap = await createImageBitmap(new Blob([raw], { type: 'image/webp' }),
    { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  return { width: bitmap.width, height: bitmap.height, source: bitmap };
}

/**
 * The whole decode, on whichever thread holds the bytes.
 *
 * One implementation for both threads, the way plotField.ts's exported
 * `buildFieldBuffers` is, so a tile's numbers cannot depend on where it was
 * decoded. A thread that cannot make an ImageBitmap hands the bytes back
 * instead of failing, and the caller finishes on the thread that can.
 */
export async function decodeTilePayloads(
  key: TileKey,
  radiusM: number,
  height: ArrayBuffer | null,
  albedo: ArrayBuffer | null,
  claimState: ClaimState | null = null,
  artificial: ArrayBuffer | null = null,
): Promise<DecodedTilePayloads> {
  // The artificial tile is the same WebP the albedo role writes, alpha and
  // all, so it takes the same decode - premultiply off included, since its
  // alpha is which texels it HAS and not an opacity.
  const decodeImage = async (
    bytes: ArrayBuffer | null,
  ): Promise<{ bitmap: DecodedAlbedo | null; raw: ArrayBuffer | null }> => {
    if (!bytes) return { bitmap: null, raw: null };
    if (typeof createImageBitmap !== 'function') return { bitmap: null, raw: bytes };
    try {
      return { bitmap: await decodeAlbedo(bytes), raw: null };
    } catch {
      return { bitmap: null, raw: bytes };
    }
  };
  const { bitmap, raw } = await decodeImage(albedo);
  const art = await decodeImage(artificial);
  // Static, unlike every other h3-js touch point (claims.ts, hexOverlay.ts):
  // this runs inside the tile worker too, and a worker's iife bundle cannot
  // code-split on a dynamic import.
  let claims: TileClaims | null = null;
  if (claimState) {
    const claimed = new Set(claimState.cells);
    claims = buildClaimTexture(key, radiusM, claimState.res, claimed, (latDeg, lonDeg, res) => {
      const lon = wrap180HalfOpen(lonDeg);
      try {
        return h3.latLngToCell(latDeg, lon, res);
      } catch {
        return null;
      }
    });
  }
  return {
    height: height ? dequantizeH16(height) : null,
    albedo: bitmap,
    albedoRaw: raw,
    artificial: art.bitmap,
    artificialRaw: art.raw,
    bounds: boundsOf(key, radiusM),
    claims,
  };
}
